import React, { useState, useEffect, useRef, useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';

import { connect } from 'react-redux';
import { Helmet } from "react-helmet";
import { Redirect } from 'react-router-dom';

// api
import useFetchStartupData from './Hooks/useFetchStartupData';

// constants
import { HUBSPOT_FORM_SOURCE } from '/src/lib/constants';
import { MAGNITT_ACCESS_ROLES } from '/src/lib/constants/userRoles';
import { WEB_URL } from '/src/lib/endpoints';

// helpers
import { getStartupBasicInfoCardDetails, getLocationDetails, getSocialInfoDetails, getStartupIndustryDetails, getPercentInView, handleMobileActiveSection, getNewsLinks, getTechnologiesUsed } from './Reusable/Helpers/sice-profile-helpers';
import { getItemWebUrl, deserializeData } from "/src/lib/helpers";
import * as StartupHelpers from './Helpers/startupHelper';
import { handleRedirectMessage } from '/src/components/Reusable/toast';

// reusable for single sice
import CompanyOverview from './Reusable/CompanyOverview';
import ICEBasicInfoCard from './Reusable/ICEBasicInfoCard';
import NewsPreviewCard from './Reusable/NewsPreviewCard';
import TeamMembersSection from '/src/components/TeamMembers/TeamMembers';
import StartupsSection from './Reusable/StartupsSection';
import ContactSalesPopup from '/src/components/ContactForms/ContactSalesPopup';
import DynamicStartupBenchmarking from './Reusable/DynamicStartupBenchmarking';
import ProfileNavigationBar from './Reusable/ProfileNavigationBar';
import { renderLockedBenchmarkingTool } from '../LockedContent';
import FundingEvolutionChart from '/src/components/StartupSections/FundingEvolution/FundingEvolutionChart';
import { FundingTable } from './Reusable/FundingRounds/FundingTableHandler';
import QuestionsSection from './Reusable/QuestionsSection';
import Skeleton from 'react-loading-skeleton';

//page reusable componenets
import ProfileHeader from './Reusable/ProfileHeader';


const StartupPage = (props) => {
    const { staticContext, match: { params }, user, history } = props;
    const isLoggedIn = user.is_loggedin;
    let startupData = {};

    if (typeof window !== 'undefined') {
        startupData = deserializeData(window.__INITIAL_DATA__);
        delete window.__INITIAL_DATA__;
    } else {
        startupData = deserializeData(staticContext.data);
    }

    const slug = props.match.params.slug.split('-');
    const entityId = parseInt(slug.pop());
    const entityType = 'startup';

    const [state, setState] = useState({
        startup: startupData,
        industries: [],
        subIndustries: [],
        businessType: null,
        support: null,
        startupBenchmarking: [],
        isBenchmarkingLoading: true,
        showAllRounds: false,
        fundingRounds: null,
        geoFocusData: [],
        hasDirAccess: false,
        owner: false,
        userroles: false,
        newsLinks: [],
        contactSalesPopUpSource: HUBSPOT_FORM_SOURCE.single_profile_locked_sections,
    });

    const [fetchData, isStartupLoading] = useFetchStartupData(entityId, entityType, isLoggedIn, setState);

    useEffect(() => {
        // access check
        document.body.style.overflowY = 'scroll';
        const hasAccess = (user && (user.roles.includes(MAGNITT_ACCESS_ROLES.directory_access) || user.roles.includes(MAGNITT_ACCESS_ROLES.limited_directory_access)));
        if (hasAccess) {

            setState((prevState) => ({
                ...prevState,
                hasDirAccess: true
            }));
        }

        if (isLoggedIn && startup?.id) {
            getNewsLinks(startup.id, state.newsLinks)
        }

        handleRedirectMessage(props.location);

        // get startup benchmarking
        StartupHelpers.getStartupBenchmarkingData(entityId, hasAccess).then(
            response => {
                setState((prevState) => ({
                    ...prevState,
                    startupBenchmarking: response,
                    isBenchmarkingLoading: false,
                }));
            }
        ).catch(() => {
            setState((prevState) => ({
                ...prevState,
                isBenchmarkingLoading: false,
            }));
        })
    }, []);

    // startup navigation section
    const sectionRefs = {
        overview: { ref: useRef(null), name: 'Overview' },
        funding: {
            ref: useRef(null),
            name: 'Funding',
            componentsIds: [
                'section-funding',
                'section-fundingrounds'
            ]
        },
        benchmarking: {
            ref: useRef(null),
            name: 'Benchmarking Tool',
            componentsIds: [
                'section-benchmarking'
            ]
        },
        investments: {
            ref: useRef(null),
            name: 'Investments',
            componentsIds: [
                'section-startupinvestments'
            ]
        },
        acquisitions: {
            ref: useRef(null),
            name: 'Acquisitions',
            componentsIds: [
                'section-startupacquisitions'
            ]
        },
        startups: {
            ref: useRef(null),
            name: 'Investors',
            componentsIds: [
                'section-startup-relations'
            ]
        },
        teams: {
            ref: useRef(null),
            name: 'Teams',
            componentsIds: [
                'section-teams'
            ]
        },
        questions: {
            ref: useRef(null),
            name: 'FAQs' ,
            componentsIds: [
                'section-questions'
            ]
        },
    };

    // redirect to 404 if entityId is not found
    // this must be before all checks for startup information
    if (isEmpty(state.startup) || state.startup.id === null) {
        return <Redirect to="/not-found" />
    }

    const { startup } = state;
    const startupURL = getItemWebUrl("startups", startup.id, startup.code);
    const metadescription = StartupHelpers.getMetaDescription(startup?.description);
    const basicInfoDetails = getStartupBasicInfoCardDetails(startup, state.businessType);
    const startupIndustryDetails = getStartupIndustryDetails(startup, state.industries, state.subIndustries);
    const locationDetails = getLocationDetails(startup, user);
    const socialInfoDetails = getSocialInfoDetails(startup);
    const technologyUsed = getTechnologiesUsed(startup);
    const newsLinks = state.newsLinks;
    let canViewChart = state.owner || state.hasDirAccess;

    const fundingTableProps = {
        entityData: state.startup,
        entityType,
        entityId,
        user,
        owner: state.owner,
        isTeamMember: isStartupLoading ? '' : state.owner,
        isTeamMemberLoading: isStartupLoading,
        setState: (newState) => setState(prev => ({ ...prev, ...newState })),
    };

    return (
        <div className="footer-margin-offset">
            <div className="sice-page content-box">
                <Helmet
                    title={`${startup.name} Company and Investment Profile | MAGNiTT`}
                    link={[
                        {
                            rel: 'canonical',
                            href: `${WEB_URL}/${startupURL}`
                        }
                    ]}
                    meta={[
                        {
                            name: 'description',
                            content: metadescription
                        },
                        {
                            name: 'abstract',
                            content: metadescription
                        },
                        {
                            property: 'og:type',
                            content: 'LocalBusiness'
                        },
                        {
                            property: 'og:url',
                            content: `${WEB_URL}/${startupURL}`
                        },
                        {
                            property: 'og:title',
                            content: startup.name
                        }
                    ]}
                    script={[
                        {
                            'type': 'application/ld+json',
                            'innerHTML': `{
                                "@context": "http://schema.org",
                                "@type": "BreadcrumbList",
                                "itemListElement": [{
                                    "@type": "ListItem",
                                    "position": 1,
                                    "name": "startups",
                                    "item": "${WEB_URL}/startups"
                                },
                                {
                                    "@type": "ListItem",
                                    "position": 2,
                                    "name": "${startup.name}",
                                    "item": "${WEB_URL}/${startupURL}"
                                }]
                            }`
                        }
                    ]}
                />
                <div className="content-block">
                    <ProfileHeader
                        entityId={entityId}
                        siceEntity={startup}
                        entityType={entityType}
                        history={history}
                        owner={state.owner}
                        user={user}
                        page={HUBSPOT_FORM_SOURCE.single_profile_locked_contacts}
                        setState={(newState) => setState(prev => ({ ...prev, ...newState }))}
                    />
                    <div className="row">
                        <div className="right-side col-12 col-xl-9 mb-0">
                            <ProfileNavigationBar
                                sectionRefs={sectionRefs}
                                entityType={entityType}
                                isLoading={isStartupLoading}
                            />
                            {isStartupLoading
                                ? (
                                    <div className="profile-section-box company-overview" ref={sectionRefs.overview.ref} style={{ paddingTop: 0, display: 'flex' }}>
                                        <div className="general-overview general-overview--investor">
                                            <div className="general-overview__content">
                                                <h2 className="profile-section-heading">General Overview</h2>
                                                <Skeleton height={14} style={{ marginBottom: 10 }} />
                                                <Skeleton height={14} width="92%" style={{ marginBottom: 10 }} />
                                                <Skeleton height={14} width="88%" style={{ marginBottom: 10 }} />
                                                <Skeleton height={14} width="75%" />
                                            </div>
                                        </div>
                                        <div className="fact-sheet">
                                            <div style={{ marginBottom: 16 }}>
                                                <Skeleton width={140} height={14} style={{ marginBottom: 10 }} />
                                                <Skeleton height={13} style={{ marginBottom: 6 }} />
                                                <Skeleton height={13} width="80%" />
                                            </div>
                                            <Skeleton width={130} height={14} style={{ marginBottom: 12 }} />
                                            <div className="section-row">
                                                {[1, 2, 3].map(n => (
                                                    <div key={n} className="investor-overview">
                                                        <Skeleton width={80} height={11} style={{ marginBottom: 6 }} />
                                                        <Skeleton width={55} height={20} />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )
                                : <CompanyOverview
                                    entityDetails={startup}
                                    fundingData={state.fundingRounds}
                                    entityType={entityType}
                                    sectionref={sectionRefs.overview.ref}
                                    user={user}
                                />
                            }

                            <FundingEvolutionChart
                                fundingRounds={state.fundingRounds}
                                isLoggedIn={isLoggedIn}
                                isStartupLoading={isStartupLoading}
                                canViewChart={canViewChart}
                                entityType={entityType}
                                sectionRef={sectionRefs.funding.ref}
                                onLockedStateChange={(newState) => setState({ ...state, ...newState })}
                            />

                            <FundingTable {...fundingTableProps} type="fundingRounds" ref={sectionRefs.funding.ref} />

                            {state.isBenchmarkingLoading ? (
                                <div className="block team startup-benchmarking profile-section-box" ref={sectionRefs.benchmarking.ref} id="section-benchmarking">
                                    <div className="title">
                                        <h2 className="profile-section-heading">Startup Benchmarking Tool</h2>
                                    </div>
                                    <Skeleton height={40} borderRadius={4} style={{ marginBottom: 20 }} />
                                    <div style={{ display: 'flex', gap: 12 }}>
                                        {[1, 2, 3].map(n => (
                                            <div key={n} style={{ flex: '1 1 0', minWidth: 0 }}>
                                                <Skeleton height={64} style={{ marginBottom: 8 }} />
                                                <Skeleton height={56} style={{ marginBottom: 6 }} />
                                                <Skeleton height={56} style={{ marginBottom: 6 }} />
                                                <Skeleton height={44} style={{ marginBottom: 6 }} />
                                                <Skeleton height={44} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : state.startupBenchmarking?.length > 0 ? (
                                <DynamicStartupBenchmarking
                                    data={state.startupBenchmarking}
                                    id={entityId}
                                    user={user}
                                    startupName={startup.name}
                                    sectionref={sectionRefs.benchmarking.ref}
                                />
                            ) : (
                                renderLockedBenchmarkingTool(entityType, entityId, (newState) => setState(prev => ({ ...prev, ...newState })), startup?.name, sectionRefs?.benchmarking.ref, user)
                            )}

                            <div ref={sectionRefs.investments.ref} style={{ marginTop: '24px' }}>
                                <FundingTable {...fundingTableProps} type="startupInvestments" ref={sectionRefs.investments.ref} />
                            </div>

                            <div ref={sectionRefs.acquisitions.ref} style={{ marginTop: '24px' }}>
                                <FundingTable {...fundingTableProps} type="startupAcquisitions" ref={sectionRefs.acquisitions.ref} />
                            </div>

                            <StartupsSection user={user}
                                entityId={entityId}
                                entityType={entityType}
                                investor={startup}
                                sectionRef={sectionRefs.startups.ref}
                            />

                            <TeamMembersSection
                                entityType={entityType}
                                entityId={entityId}
                                user={user}
                                entityName={startup?.name}
                                page={HUBSPOT_FORM_SOURCE.single_profile_locked_contacts}
                                sectionref={sectionRefs.teams.ref}
                            />

                            <QuestionsSection
                                object={startup}
                                user={user}
                                sectionref={sectionRefs.questions.ref}
                                setState={(newState) => setState(prev => ({ ...prev, ...newState }))}
                                entityType={entityType} />

                        </div>

                        <div className="leftside col-12 col-xl-3">
                            {basicInfoDetails && <ICEBasicInfoCard infoDetails={basicInfoDetails} />}
                            {technologyUsed && <ICEBasicInfoCard infoDetails={technologyUsed} />}
                            {socialInfoDetails && <ICEBasicInfoCard infoDetails={socialInfoDetails} />}
                            {startupIndustryDetails && <ICEBasicInfoCard infoDetails={startupIndustryDetails} />}
                            {locationDetails && <ICEBasicInfoCard infoDetails={locationDetails} />}
                            {newsLinks.length > 0 && <NewsPreviewCard newsLinks={newsLinks} />}
                        </div>
                    </div>
                </div>
            </div>
            {/* used for rendering popup when user clicks contact sales button */}
            <ContactSalesPopup page={state.contactSalesPopUpSource} />
        </div>
    );
};

const mapStateToProps = (state) => ({
    user: state.userReducer
});

export default connect(mapStateToProps, null)(StartupPage);
